// Drives one hold on an emoji (#714): runs the shared state machine (shared/src/jumboHold.ts) against a clock and
// a frame loop, and tells the page what to draw and what to send. It owns no DOM beyond `data-holding` on the
// pressed element; the growing emoji is drawn by HoldOverlay, which subscribes here, so a frame never re-renders
// the picker or the message list. The clock and the frame loop come in as arguments, so a test runs a whole hold on
// a fake clock.
import { holdScale, holdStep, holdStillOn, holdTremble, IDLE, type HoldSend, type HoldState } from "@bloom/shared";

/** What the overlay draws for one frame. */
export interface HoldView {
  /** The size, as a multiple of the normal size. */
  scale: number;
  /** The tremble's offset, in px. */
  x: number;
  y: number;
  /** The splat is showing (the hold ran to 6 s). */
  splat: boolean;
}

/** What the person has turned on or off, read at each frame, so a change in Settings applies at once. */
export interface HoldFlags {
  /** Settings → Reaction effects. Off: the emoji does not grow or tremble and no sound plays. */
  effects: boolean;
  /** The system's Reduce motion. On: it grows, but does not tremble, and the splat does not pop. */
  reducedMotion: boolean;
}

export interface HoldTarget {
  el: HTMLElement;
  emoji: string;
  /** A finger or pen holds it, not a mouse: it covers the emoji, so the overlay draws a bubble above it (#726). */
  touch?: boolean;
}

export interface HoldDeps {
  now: () => number;
  /** Schedules a callback for the next frame; returns what `cancel` takes. */
  frame: (cb: () => void) => number;
  cancel: (id: number) => void;
  flags: () => HoldFlags;
  /** The pointer was released and the hold sends something ("normal" is a tap: the click does it). */
  onSend: (target: HoldTarget, send: HoldSend) => void;
  /** The splat just began: play the sound. */
  onSplat: () => void;
}

const BROWSER_DEPS = (): Pick<HoldDeps, "now" | "frame" | "cancel"> => ({
  now: () => performance.now(),
  frame: (cb) => requestAnimationFrame(cb),
  cancel: (id) => cancelAnimationFrame(id),
});

export const browserFlags = (effects: () => boolean): (() => HoldFlags) => () => ({
  effects: effects(),
  reducedMotion: !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches,
});

export class HoldDriver {
  private state: HoldState = IDLE;
  private frameId: number | null = null;
  private listeners = new Set<() => void>();
  target: HoldTarget | null = null;
  /** The frame to draw; null while there is nothing to draw (before the grow starts, or after the hold ends). */
  view: HoldView | null = null;

  constructor(private deps: HoldDeps) {}

  static browser(deps: Omit<HoldDeps, "now" | "frame" | "cancel">): HoldDriver {
    return new HoldDriver({ ...BROWSER_DEPS(), ...deps });
  }

  subscribe = (fn: () => void) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };

  get holding() {
    return this.state.phase === "held";
  }

  /** Milliseconds since the press; 0 when nothing is held. */
  get elapsed() {
    return this.state.phase === "held" ? this.deps.now() - this.state.since : 0;
  }

  /**
   * The pointer moved or lifted at `x, y` (client px). Off the emoji it is a leave (`holdStillOn`: a held finger may drift
   * HOLD_SLOP_PX outside it). Returns whether the hold goes on.
   */
  moved(x: number, y: number): boolean {
    const target = this.target;
    if (!this.holding || !target) return false;
    const r = target.el.getBoundingClientRect();
    if (holdStillOn(x, y, [r.left, r.top, r.width, r.height], !!target.touch, this.elapsed)) return true;
    this.leave();
    return false;
  }

  private emit() {
    for (const fn of this.listeners) fn();
  }

  press(target: HoldTarget) {
    if (this.holding) return;
    this.target = target;
    this.state = holdStep(this.state, { t: "press", at: this.deps.now() }).state;
    this.view = null;
    this.frameId = this.deps.frame(this.tick);
  }

  /** The pointer was let go. Returns what it sent, so the caller can swallow the click that follows. */
  release(): HoldSend | "none" | "splat" {
    const target = this.target;
    if (!this.holding || !target) return "none";
    const r = holdStep(this.state, { t: "release", at: this.deps.now() });
    if (r.splat) this.splat();
    this.end();
    if (r.send) {
      this.deps.onSend(target, r.send);
      return r.send;
    }
    return "splat";
  }

  /** The pointer left the emoji. */
  leave() {
    this.stop("leave");
  }

  /** The touch was cancelled. */
  cancel() {
    this.stop("cancel");
  }

  private stop(t: "leave" | "cancel") {
    if (!this.holding) return;
    this.state = holdStep(this.state, { t, at: this.deps.now() }).state;
    this.end();
  }

  private end() {
    if (this.frameId !== null) this.deps.cancel(this.frameId);
    this.frameId = null;
    this.state = IDLE;
    this.target?.el.removeAttribute("data-holding");
    this.target = null;
    this.view = null;
    this.emit();
  }

  private splat() {
    if (this.deps.flags().effects) this.deps.onSplat();
  }

  private tick = () => {
    this.frameId = null;
    if (this.state.phase !== "held" || !this.target) return;
    const at = this.deps.now();
    const r = holdStep(this.state, { t: "tick", at });
    const ms = at - this.state.since;
    this.state = r.state;
    if (r.splat) this.splat();
    this.view = this.draw(ms, r.stage === "splat");
    // The pressed emoji hides while the overlay draws its bigger copy in its place. A finger's copy is in a bubble above it.
    if (this.view && !this.target.touch) this.target.el.setAttribute("data-holding", "");
    this.emit();
    // After the splat nothing changes any more: wait for the release.
    if (r.stage !== "splat") this.frameId = this.deps.frame(this.tick);
  };

  /** What to draw at `ms`, with the person's settings applied. */
  private draw(ms: number, splat: boolean): HoldView | null {
    const { effects, reducedMotion } = this.deps.flags();
    // Effects off: no growing and no tremble. The splat still shows (it says nothing was sent), without a sound.
    const scale = effects ? holdScale(ms) : 1;
    const t = effects && !reducedMotion && !splat ? holdTremble(ms) : { x: 0, y: 0 };
    if (!splat && scale === 1) return null;
    return { scale, x: t.x, y: t.y, splat };
  }
}
